import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { environment } from "../../environments/environment";
import { GateInRequest } from "../models/gate-in-request.interface";
import { TableListRequest } from "../models/table-list-request.interface";
import { Tally } from "../models/tally.interface";
import { InventoryType } from "../constants/app-constants";
import { delay, forkJoin, Observable, of } from "rxjs";
import { ShippingBill } from "../models/shipping-bill.interface";
import { APIResponse } from "../models/standard-response.interface";
import { ActivityWorkflow } from "../models/workflow.interface";
import { WeighingRecord } from "../models/weighing-record.interface";
import { ReeferTemperatureRequest } from "../models/reefer-job.interface";
import { RequiresPermissions } from "../decorators/evealuate-permission.decorator";
import { PermissionConstants } from "../constants/permission-constants";
import { GateInPinResolution } from "../models/gate-in-pin-resolution.interface";

const operationsEndPoint = "v1/operations";

const cargoItemsEndPoint = "v1/import-cargo-items";
const trailerInventoryEndPoint = "v1/inventory/trailers";
const containerInventoryEndPoint = "v1/containers";
const igmContainersEndPoint = "v1/igm-containers";
const jobOrdersEndpoint = "v1/job-orders";
const jobOrderInventoriesEndpoint = "v1/job-order-inventories";
const tallyEndPoint = "v1/tally";
const holdItemsEndPoint = "v1/hold-items";
const activityWorkflowEndpoint = "v1/activity-workflows";
const leoUpdateEndPoint = "v1/leo-updates";
const weighingRecordsEndPoint = "v1/weighing-records";
const reeferJobEndPoint = "v1/reefer-temperature-logs";

@Injectable({
  providedIn: "root"  // Ensures the service is available app-wide
})
export class OperationAPIService {

  private baseUrl: string = environment.apiBaseUrl;

  constructor(private http: HttpClient) {
  }

  submitGateInRequest(gateInRequest: GateInRequest) {
    return this.http.post(
      `${this.baseUrl}/${operationsEndPoint}/gate-in`,
      gateInRequest
    );
  }

  submitGateOutRequest(gateOutRequest: any) {
    return this.http.post(
      `${this.baseUrl}/${operationsEndPoint}/gate-out`,
      gateOutRequest
    );
  }

  resolveGateInPin(pin: string): Observable<APIResponse> {
    return this.http.post<APIResponse>(
      `${this.baseUrl}/${operationsEndPoint}/gate-in/resolve-pin`,
      { pin }
    );
  }

  resolveGateOutPin(pin: string): Observable<APIResponse> {
    return this.http.post<APIResponse>(
      `${this.baseUrl}/${operationsEndPoint}/gate-out/resolve-pin`,
      { pin }
    );
  }

  submitPreGateInRequest(preGateInRequest: any): Observable<APIResponse> {
    return this.http.post<APIResponse>(`${this.baseUrl}/${operationsEndPoint}/pre-gate-in`, preGateInRequest);
  }

  fetchRecentData() {
    return this.http.get(`${this.baseUrl}/${igmContainersEndPoint}/fetch-recent-data`);
  }

  deleteIgmContainersByBlNo(blNo: string): Observable<APIResponse> {
    return this.http.delete<APIResponse>(`${this.baseUrl}/${igmContainersEndPoint}/by-bl/${encodeURIComponent(blNo)}`);
  }

  getBlContents(blNo: string): Observable<APIResponse> {
    return this.http.get<APIResponse>(`${this.baseUrl}/${igmContainersEndPoint}/by-bl/${encodeURIComponent(blNo)}`);
  }

  getData(apirUrl: string) {
    return this.http.get(`${this.baseUrl}/${apirUrl}`);
  }

  submitItemFilingForm(cargoItem: any) {
    return this.http.post(
      `${this.baseUrl}/${cargoItemsEndPoint}`,
      cargoItem
    );
  }

  updateItemFilingForm(cargoItemId: string, cargoItem: any) {
    return this.http.put(
      `${this.baseUrl}/${cargoItemsEndPoint}/${cargoItemId}`,
      cargoItem
    );
  }

  getImportCargoById(importCargoId: string, withContainers: boolean = false, includeHoldDetails: boolean = true) {
    return this.http.get(`${this.baseUrl}/${cargoItemsEndPoint}/${importCargoId}?withContainers=${withContainers}&includeHoldDetails=${includeHoldDetails}`);
  }

  getImportCargoByContainerId(containerId: string, entryContainer: boolean = true) {
    return this.http.get(`${this.baseUrl}/${containerInventoryEndPoint}/${containerId}/import-cargo-items?withContainers=${entryContainer}`);
  }

  downloadWeighingSlipForContainer(containerId: string) {
    return this.http.get(`${this.baseUrl}/${containerInventoryEndPoint}/${containerId}/download-weighing-slip`);
  }

  getImportCargoList(tableListRequest: TableListRequest) {
    return this.http.post(`${this.baseUrl}/${cargoItemsEndPoint}/list`, tableListRequest);
  }

  // Job Orders

  getJobOrdersList(tableListRequest: TableListRequest): Observable<APIResponse> {
    return this.http.post<APIResponse>(`${this.baseUrl}/${jobOrdersEndpoint}/list`, tableListRequest);
  }

  saveJobOrder(jobOrderRequest: any) {
    return this.http.post(
      `${this.baseUrl}/${jobOrdersEndpoint}`,
      jobOrderRequest
    );
  }

  updateJobOrder(jobOrderId: string, jobOrderRequest: any): Observable<any> {
    return this.http.put(
      `${this.baseUrl}/${jobOrdersEndpoint}/${jobOrderId}`,
      jobOrderRequest
    );
  }

  cancelJobOrder(jobOrderId: string): Observable<any> {
    return this.http.patch(
      `${this.baseUrl}/${jobOrdersEndpoint}/${jobOrderId}/cancel`, null);
  }

  markAsCompleted(jobOrderId: string): Observable<any> {
    return this.http.patch(
      `${this.baseUrl}/${jobOrdersEndpoint}/${jobOrderId}/complete`, null);
  }

  downloadGatePass(jobOrderId: string, selection: { containerIds: string[]; truckNos: string[] }): Observable<APIResponse> {
    return this.http.post<APIResponse>(`${this.baseUrl}/${jobOrdersEndpoint}/${jobOrderId}/print-pdf/gate-pass`, {
      containerIds: selection.containerIds ?? [],
      truckNos: selection.truckNos ?? []
    });
  }

  downloadJobOrderPdf(jobOrderId: string): Observable<APIResponse> {
    return this.http.get<APIResponse>(`${this.baseUrl}/${jobOrdersEndpoint}/${jobOrderId}/print-pdf`);
  }

  getGatePassPrintOptions(jobOrderId: string): Observable<APIResponse> {
    return this.http.get<APIResponse>(`${this.baseUrl}/${jobOrdersEndpoint}/${jobOrderId}/print-pdf/gate-pass/options`);
  }

  fetchJobOrderByBookingNo(bookingNo: string, jobOrderType?: string) {
    if (jobOrderType) {
      return this.http.get(`${this.baseUrl}/${jobOrdersEndpoint}/find-by-booking-no?bookingNo=${bookingNo}&type=${jobOrderType}`);
    }
    return this.http.get(`${this.baseUrl}/${jobOrdersEndpoint}/find-by-booking-no?bookingNo=${bookingNo}`);
  }

  fetchJobOrderInventoryDetails(jobOrderId: string) {
    return this.http.get(`${this.baseUrl}/${jobOrdersEndpoint}/${jobOrderId}/inventory`);
  }

  signPO(jobOrderId: string) {
    return this.http.patch(`${this.baseUrl}/${jobOrdersEndpoint}/${jobOrderId}/po-endorsement`, {});
  }

  getJobOrderInventoriesList(tableListRequest: TableListRequest) {
    return this.http.post(`${this.baseUrl}/${jobOrderInventoriesEndpoint}/search`, tableListRequest);
  }

  updateContainerTrailerMapping(request: any): Observable<APIResponse> {
    return this.http.post<APIResponse>(
      `${this.baseUrl}/${operationsEndPoint}/trailer-assignment`,
      request
    );
  }

  fetchJORelatedInventories<T extends { inventoryId: string; inventoryType: string }>(
    jobOrderData: any,
    services: {
      containerService?: (ids: string[]) => Observable<any>,
      cargoService?: (ids: string[]) => Observable<any>,
      vehicleService?: (ids: string[]) => Observable<any>
    }
  ): Observable<{ jobOrder: any, containers?: any, cargos?: any, vehicles?: any }> {

    const forkJoinPayload: {
      jobOrder: Observable<any>,
      containers?: Observable<any>,
      cargos?: Observable<any>,
      vehicles?: Observable<any>
    } = {
      jobOrder: of(jobOrderData)
    };

    if (services.containerService) {
      const containerIds = jobOrderData.jobOrderInventoryList
        ?.filter((inv: T) => inv.inventoryType === "CONTAINER")
        ?.map((inv: T) => inv.inventoryId) ?? [];
      if (containerIds.length) {
        forkJoinPayload.containers = services.containerService(containerIds);
      }
    }

    if (services.cargoService) {
      const cargoIds = jobOrderData.jobOrderInventoryList
        ?.filter((inv: T) => inv.inventoryType === "CARGO")
        ?.map((inv: T) => inv.inventoryId) ?? [];
      if (cargoIds.length) {
        forkJoinPayload.cargos = services.cargoService(cargoIds);
      }
    }

    if (services.vehicleService) {
      const vehicleIds = jobOrderData.jobOrderInventoryList
        ?.filter((inv: T) => inv.inventoryType === InventoryType.TRAILER.valueOf() || inv.inventoryType === InventoryType.TRUCK.valueOf())
        ?.map((inv: T) => inv.inventoryId) ?? [];
      if (vehicleIds.length) {
        forkJoinPayload.vehicles = services.vehicleService(vehicleIds);
      }
    }

    return forkJoin(forkJoinPayload);
  }


  getJobOrderByIdLite(jobOrderId: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/${jobOrdersEndpoint}/${jobOrderId}`);
  }

  getJobOrderDetails(jobOrderId: string, jobInventoryId?: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/${jobOrdersEndpoint}/details/${jobOrderId}${jobInventoryId ? `?jobInventoryId=${jobInventoryId}` : ''}`);
  }

  getJobOrderById(jobOrderId: string, includeItems: boolean = false, includeVendors: boolean = false, includeContainers: boolean = false): Observable<any> {
    return this.http.get(`${this.baseUrl}/${jobOrdersEndpoint}/${jobOrderId}?includeItems=${includeItems}&includeVendors=${includeVendors}&includeContainers=${includeContainers}`);
  }

  fetchJobOrdersByInventoryIdOrNo(inventoryIdOrNo: string, byInventoryNo: boolean = false, forGatePass: boolean = false) {
    return this.http.get(`${this.baseUrl}/${jobOrdersEndpoint}/find-by-inventory/${inventoryIdOrNo}?byInventoryNo=${byInventoryNo}&forGatePass=${forGatePass}`);
  }

  // Tally

  saveTally(tallyData: Tally) {
    return this.http.post(
      `${this.baseUrl}/${tallyEndPoint}`,
      tallyData
    );
  }

  getTallyDetailsById(tallyId: String) {
    return this.http.get(`${this.baseUrl}/${tallyEndPoint}/${tallyId}`);
  }

  listTalliesByJobOrder(jobOrderId: string) {
    return this.http.get(`${this.baseUrl}/${tallyEndPoint}/list-by-job-order?jobOrderId=${jobOrderId}`);
  }

  listTally(tableListRequest: TableListRequest) {
    return this.http.post(`${this.baseUrl}/${tallyEndPoint}/list`, tableListRequest);
  }
  // Hold

  listHoldRecords(tableListRequest: TableListRequest) {
    return this.http.post(`${this.baseUrl}/${holdItemsEndPoint}/list`, tableListRequest);
  }

  listItemsForHold(tableListRequest: TableListRequest, cycle: string) {
    return this.http.post(`${this.baseUrl}/${holdItemsEndPoint}/list/items-for-hold?processCycle=${cycle}`, tableListRequest);
  }

  submitHoldRequest(holdRequest: any) {
    return this.http.post(`${this.baseUrl}/${holdItemsEndPoint}`, holdRequest);
  }

  submitHoldReleaseRequest(holdReleaseRequest: any) {
    return this.http.post(`${this.baseUrl}/${holdItemsEndPoint}/release`, holdReleaseRequest);
  }

  // Activity workflows
  createActivityWorkflow(request: ActivityWorkflow): Observable<APIResponse> {
    return this.http.post<APIResponse>(`${this.baseUrl}/${activityWorkflowEndpoint}`, request);
  }

  updateActivityWorkflow(id: string, request: ActivityWorkflow): Observable<APIResponse> {
    return this.http.put<APIResponse>(`${this.baseUrl}/${activityWorkflowEndpoint}/${id}`, request);
  }

  getActivityWorkflow(id: string): Observable<APIResponse> {
    return this.http.get<APIResponse>(`${this.baseUrl}/${activityWorkflowEndpoint}/${id}`);
  }

  listActivities(tableListRequest: TableListRequest): Observable<APIResponse> {
    return this.http.post<APIResponse>(`${this.baseUrl}/${activityWorkflowEndpoint}/list`, tableListRequest);
  }

  listActivityOptions(): Observable<APIResponse> {
    return this.http.get<APIResponse>(`${this.baseUrl}/${activityWorkflowEndpoint}/activities`);
  }

  updateActivityStatus(shouldSetActive: boolean, idList: string[]): Observable<APIResponse> {
    return this.http.patch<APIResponse>(
      `${this.baseUrl}/${activityWorkflowEndpoint}/status?status=${shouldSetActive}`,
      idList
    );
  }

  getCurrentWeightFromBridge(bridgeId: string): Observable<APIResponse> {
    return this.http.get<APIResponse>(`${this.baseUrl}/v1/weighing-bridges/${bridgeId}/raw-data`);
  }

  //WEIGHMENT
  getWeighmentRecordsNewEntry(inventoryType: string,inventoryNumber: String) {
    return this.http.get<APIResponse>(`${this.baseUrl}/${weighingRecordsEndPoint}/new-entry?inventoryType=${inventoryType}&inventoryNumber=${inventoryNumber}`);
  }

  saveWighmentRecord(weighingRecord: WeighingRecord) {
    return this.http.post<APIResponse>(`${this.baseUrl}/${weighingRecordsEndPoint}`, weighingRecord);
  }

  appendRecord(id: string,weighingRecord: WeighingRecord) {
    return this.http.put<APIResponse>(`${this.baseUrl}/${weighingRecordsEndPoint}/${id}`,weighingRecord);
  }

  getWeighmentRecord(id: string){
    return this.http.get<APIResponse>(`${this.baseUrl}/${weighingRecordsEndPoint}/${id}`);
  }

  cancelWeighingRecord(id: string){
    return this.http.post<APIResponse>(`${this.baseUrl}/${weighingRecordsEndPoint}/${id}/cancel`,null);
  }

  @RequiresPermissions({ permissionIds: [PermissionConstants.WEIGHMENT.LIST]})
  listWieghments(tableListRequest: TableListRequest) {
    return this.http.post<APIResponse>(`${this.baseUrl}/${weighingRecordsEndPoint}/list`,tableListRequest);
  }

  // Reefer

  listReeferJobs(tableListRequest: TableListRequest): Observable<APIResponse> {
    return this.http.post<APIResponse>(`${this.baseUrl}/${reeferJobEndPoint}/list-jobs`, tableListRequest);
  }

  listReeferLogs(jobId: string, tableListRequest: TableListRequest): Observable<APIResponse> {
    return this.http.post<APIResponse>(`${this.baseUrl}/${reeferJobEndPoint}/${jobId}/logs`, tableListRequest);
  }

  fetchReeferContainerDetails(containerId : string): Observable<APIResponse> {
    return this.http.get<APIResponse>(`${this.baseUrl}/${reeferJobEndPoint}/${containerId}/fetch-details`);
  }

  recordTemperature(request : ReeferTemperatureRequest): Observable<APIResponse> {
    return this.http.post<APIResponse>(`${this.baseUrl}/${reeferJobEndPoint}`, request);
  }

  plugOutReefer(jobId : string, request : ReeferTemperatureRequest): Observable<APIResponse> {
    return this.http.patch<APIResponse>(`${this.baseUrl}/${reeferJobEndPoint}/${jobId}/plug-out`, request);
  }

  downloadShippingBillCLP(jobOrderId: string, cargoId: string): Observable<APIResponse> {
    return this.http.get<APIResponse>(`${this.baseUrl}/${jobOrdersEndpoint}/print-pdf/clp/${jobOrderId}/cargos/${cargoId}`);
  }

  downloadContainerCLP(jobOrderId: string, containerId: any) {
    return this.http.get<APIResponse>(`${this.baseUrl}/${jobOrdersEndpoint}/print-pdf/clp/${jobOrderId}/containers/${containerId}`);
  }

  closeForceGateOutContainers(request: { containerIds: string[] }): Observable<APIResponse> {
    return this.http.post<APIResponse>(
      `${this.baseUrl}/${operationsEndPoint}/force-gateout/close`,
      request
    );
  }

  fetchVehicleEntry(trailerMasterId: string): Observable<APIResponse> {
    return this.http.get<APIResponse>(`${this.baseUrl}/${operationsEndPoint}/pre-gate-in/${trailerMasterId}`);
  }

  markForceGateOut(request: { containers: Array<{ containerId: string; approvedBy?: string | null; remarks?: string | null }> }): Observable<APIResponse> {
    return this.http.post<APIResponse>(`${this.baseUrl}/${operationsEndPoint}/mark-force-gateout`, request);
  }

  downloadWeighmentSlip(id: any) {
    return this.http.get(`${this.baseUrl}/${weighingRecordsEndPoint}/${id}/print-pdf`);
  }
}

